import { Positions, TreeDefaultNodeIds } from "../../utils/constants";

class Tree {
  constructor(data = null) {
    if (data) {
      const temp = { ...data };
      this._addMetaData(temp);

      this.allData = temp;
      this.activeSubTree = { ...temp };
      this.activeSubTreeId = temp.id;
    } else {
      this.allData = {};
      this.activeSubTree = {};
    }
  }

  setActiveSubTree(id) {
    this.activeSubTreeId = id;
    const data = this.getNode(this.allData, id);
    this.activeSubTree = { ...data };
  }

  addNode(node, parentId = null, index = null) {
    node = { ...node };
    const parent = this.getNode(this.allData, parentId);

    this._addMetaData(node, [...parent.parents, parent.id], parent.level + 1);

    if (index === null) index = parent.children.length + 1;

    const children = [...parent.children];
    children.splice(index, 0, node);

    this.setValue(parentId, "expanded", true);
    this.setValue(parentId, "children", children);
  }
  removeNode(id) {
    const node = this.getNode(this.allData, id);

    const nearestParentId = node.parents[node.parents.length - 1];
    const parent = this.getNode(this.allData, nearestParentId);

    const children = parent.children.filter((child) => child.id !== id);

    this.setValue(parent.id, "children", children);
  }
  setValue(id, key, value) {
    const newTree = deepClone(this.allData);
    this._deepSet(newTree, id, key, value);

    this.allData = newTree;
    // Refresh active tree
    this.setActiveSubTree(this.activeSubTreeId);
  }
  getNode(tree, id) {
    // Check current level
    if (tree.id === id) return tree;

    for (let i = 0; i < tree.children.length; i++) {
      let item = this.getNode(tree.children[i], id);
      if (item) return item;
    }
  }
  getNodeByItemId(tree, itemId) {
    // Check current level
    if (tree.itemId === itemId) return tree;

    for (let i = 0; i < tree.children.length; i++) {
      let item = this.getNodeByItemId(tree.children[i], itemId);
      if (item) return item;
    }
  }
  // Used to select active player. arrow up and down functionality
  getVisibleNodeIds(tree, newArr = []) {
    if (tree.hide) return newArr;

    if (tree.id !== TreeDefaultNodeIds.Root) newArr.push(tree.id);
    if (!tree.expanded) return newArr;

    for (let i = 0; i < tree.children.length; i++) {
      this.getVisibleNodeIds(tree.children[i], newArr);
    }

    return newArr;
  }

  moveNodeUp(id) {
    const node = this.getNode(this.allData, id);

    const parentIndex = node.parents[node.parents.length - 1];
    const parent = this.getNode(this.allData, parentIndex);

    let nodeIndex = parent.children.findIndex((obj) => obj.id === node.id);
    if (nodeIndex === 0) return;

    const beforeNode = parent.children.slice(0, nodeIndex - 1);
    const afterNode = [parent.children[nodeIndex - 1], ...parent.children.slice(nodeIndex + 1)];

    const newChildren = [...beforeNode, node, ...afterNode];

    this.setValue(parent.id, "children", newChildren);
  }

  moveNodeDown(id) {
    const node = this.getNode(this.allData, id);

    const parentIndex = node.parents[node.parents.length - 1];
    const parent = this.getNode(this.allData, parentIndex);

    const lastElementIndex = parent.children.length - 1;

    let nodeIndex = parent.children.findIndex((obj) => obj.id === node.id);
    if (nodeIndex === lastElementIndex) return;

    const beforeNode = [...parent.children.slice(0, nodeIndex), parent.children[nodeIndex + 1]];
    const afterNode = [...parent.children.slice(nodeIndex + 2)];

    const newChildren = [...beforeNode, node, ...afterNode];

    this.setValue(parent.id, "children", newChildren);
  }

  moveNodeOneLevelUp(id) {
    const node = { ...this.getNode(this.allData, id) };

    const parentId = node.parents[node.parents.length - 1];
    const parent = this.getNode(this.allData, parentId);

    if (parent.id === TreeDefaultNodeIds.Root) return;

    const grandParentId = parent.parents[parent.parents.length - 1];
    const grandParent = this.getNode(this.allData, grandParentId);

    const newIndex = grandParent.children.findIndex((item) => item.id === parent.id);

    this.removeNode(id);
    this.addNode(node, grandParent.id, newIndex);
  }
  moveNodeOneLevelDown(id) {
    const node = { ...this.getNode(this.allData, id) };

    const parentId = node.parents[node.parents.length - 1];
    const parent = this.getNode(this.allData, parentId);

    const nodeIndex = parent.children.findIndex((item) => item.id === node.id);

    if (nodeIndex === parent.children.length - 1) return;

    const nextSibling = parent.children[nodeIndex + 1];

    this.removeNode(id);
    this.addNode(node, nextSibling.id, 0);
  }

  moveNode(nodeId, position, targetId) {
    if (nodeId === targetId) return;

    const node = { ...this.getNode(this.allData, nodeId) };
    const target = this.getNode(this.allData, targetId);

    // a node can not move to it's child
    if (target.parents.some((parentId) => parentId === nodeId)) return;

    this.removeNode(nodeId);

    const targetParentId = target.parents[target.parents.length - 1];
    const targetParent = this.getNode(this.allData, targetParentId);
    const targetIndex = targetParent.children.findIndex((item) => item.id === targetId);

    if (position === Positions.Before) {
      this.addNode(node, targetParentId, targetIndex);
    } else if (position === Positions.After) {
      this.addNode(node, targetParentId, targetIndex + 1);
    } else {
      this.addNode(node, targetId, 0);
    }
  }
  getAllPropertiesInSubTree(node, keys, condition, result = []) {
    // keys can be an array of keys or just a single key
    if (condition === undefined || condition(node)) {
      if (typeof keys === "object") {
        const item = {};
        keys.forEach((key) => (item[key] = node[key]));
        result.push(item);
      } else if (typeof keys === "string") {
        result.push(node[keys]);
      }
    }

    for (let i = 0; i < node.children.length; i++) {
      this.getAllPropertiesInSubTree(node.children[i], keys, condition, result);
    }

    return result;
  }
  addReference(node) {
    node.reference = node.reference?.length > 0 ? node.reference : [...node.parents];
    for (let i = 0; i < node.children.length; i++) {
      this.addReference(node.children[i]);
    }
  }
  isChildOf(parentNode, childId) {
    if (!parentNode || !parentNode.children) return false;

    for (let child of parentNode.children) {
      if (child.id === childId || this.isChildOf(child, childId)) return true;
    }
    return false;
  }
  _addMetaData(tree, parents = [], level = 0) {
    tree.parents = [...parents];
    tree.level = level;

    for (let i = 0; i < tree.children.length; i++) {
      this._addMetaData(tree.children[i], [...parents, tree.id], level + 1);
    }
  }
  removeMetaData(tree) {
    delete tree.parents;
    delete tree.level;

    for (let i = 0; i < tree.children.length; i++) {
      this.removeMetaData(tree.children[i]);
    }
  }
  _deepSet(tree, id, key, value) {
    // Check current level
    if (tree.id === id) {
      return (tree[key] = value);
    }

    for (let i = 0; i < tree.children.length; i++) {
      let changed = this._deepSet(tree.children[i], id, key, value);
      if (changed) return changed;
    }
  }
  getHashTable(data) {
    return this._getHash(data);
  }

  _getHash(object, hash = {}) {
    const tempObject = { ...object, childrenCount: object.children.length };
    delete tempObject.children;

    hash[object.id] = tempObject;

    object.children.forEach((child) => {
      this._getHash(child, hash);
    });

    return hash;
  }
  filter(filterFunction) {
    return this._filter(this.allData, filterFunction);
  }
  _filter(tree, filterFunction, result = []) {
    if (filterFunction(tree) && tree.id !== TreeDefaultNodeIds.Root) {
      const item = { ...tree };
      delete item.children;
      result.push(item);
    }

    for (let i = 0; i < tree.children.length; i++) {
      this._filter(tree.children[i], filterFunction, result);
    }

    return result;
  }
  expandParentNodes(id) {
    const node = this.getNode(this.allData, id);
    const parents = node.parents;

    for (let i = 0; i < parents.length; i++) {
      this.setValue(parents[i], "expanded", true);
    }
  }
}

export function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

export default Tree;
